import { DEFAULT_SPLIT_MODE, type Split, type SplitMode, type SplitTarget } from "./types";

const autoDistribute = (prev: Split | undefined): Split | undefined => {
  if (!prev) return prev;

  const lockedTargets = prev.targets.filter((t) => t.locked || t.disabled);
  const unlockedTargets = prev.targets.filter((t) => t.checked && !t.locked && !t.disabled);

  const totalLocked = lockedTargets.reduce((sum, t) => sum + Number(t.value || "0"), 0);
  const remaining = 100 - totalLocked;
  const perUnlocked = unlockedTargets.length > 0 ? remaining / unlockedTargets.length : 0;

  const newTargets = prev.targets.map((target) => {
    if (target.locked || target.disabled || !unlockedTargets.find((ut) => ut.id === target.id)) {
      return target;
    }

    const roundedValue = perUnlocked % 1 === 0 ? String(perUnlocked) : Number(perUnlocked).toFixed(2);
    return {
      ...target,
      rawValue: String(perUnlocked),
      value: roundedValue,
    };
  });

  return {
    ...prev,
    targets: newTargets,
  };
};

const sumTargetsValues = (targets: SplitTarget[]) =>
  targets.reduce((accumulator, currentTarget) => accumulator + (currentTarget.checked ? +currentTarget.value : 0), 0);

const selectOrigin =
  (target?: SplitTarget, targets?: SplitTarget[]) =>
  (prev: Split | undefined): Split | undefined => {
    if (!prev) {
      return prev;
    }

    if (!target || !targets) {
      return {
        ...prev,
        origin: undefined,
        mode: DEFAULT_SPLIT_MODE as SplitMode,
        targets: [],
      };
    }

    const initialTargets = targets
      .filter((ot) => ot.id !== target.id)
      .map((t) => ({
        ...t,
        value: "0",
        rawValue: "0",
      }));

    const initialState = {
      ...prev,
      origin: target,
      mode: DEFAULT_SPLIT_MODE as SplitMode,
      targets: initialTargets,
    };

    return autoDistribute(initialState);
  };

const modeChange =
  (mode: SplitMode) =>
  (prev: Split | undefined): Split | undefined => {
    if (!prev) {
      return prev;
    }

    const newState = {
      ...prev,
      mode,
      targets: prev.targets.map((target) => ({
        ...target,
        locked: mode === "proportional" ? false : target.locked,
      })),
    };

    if (mode === "proportional") {
      return autoDistribute(newState);
    }

    return newState;
  };

const checkedTarget =
  (label: string) =>
  (prev: Split | undefined): Split | undefined => {
    if (!prev) {
      return prev;
    }

    const newTargets = prev.targets.map((target) => {
      if (target.label === label) {
        const isNowChecked = !target.checked;
        return {
          ...target,
          checked: isNowChecked,
          value: "0",
          rawValue: "0",
          locked: isNowChecked ? target.locked : false,
        };
      }
      return target;
    });

    const modified = {
      ...prev,
      targets: newTargets,
    };

    return modeChange(modified.mode)(modified);
  };

const checkAllTargets =
  (checked: boolean) =>
  (prev: Split | undefined): Split | undefined => {
    if (!prev) {
      return prev;
    }

    const newTargets = prev.targets.map((target) => {
      if (target.disabled && !target.checked) {
        return target;
      }

      return {
        ...target,
        checked,
        value: !checked ? "0" : target.value,
        rawValue: !checked ? "0" : target.rawValue,
      };
    });

    const modified = { ...prev, targets: newTargets };
    return modeChange(modified.mode)(modified);
  };

const updatePercentage =
  (id: string, value: string, rawValue?: string) =>
  (prev: Split | undefined): Split | undefined => {
    if (!prev) return prev;

    return {
      ...prev,
      targets: prev.targets.map((target) =>
        target.id === id
          ? {
              ...target,
              rawValue: rawValue ?? value,
              value,
            }
          : target
      ),
    };
  };

export default {
  sumTargetsValues,
  selectOrigin,
  modeChange,
  checkedTarget,
  checkAllTargets,
  updatePercentage,
  autoDistribute,
};
